import { observer } from 'owa-mobx-react';
import { getFolderTable, getEffectiveFolderDisplayName } from 'owa-folders';
import {
    animationContainer,
    contentContainer,
} from 'owa-expansion-animation/lib/styles/expansionAnimationStyles.scss';
import { transitionStyles } from 'owa-expansion-animation';
import { CSSTransition } from 'react-transition-group';
import { getFolderViewStateFromIdNoFallback } from 'owa-mail-folder-store/lib/selectors/getFolderViewStateFromId';
import React from 'react';
import type { MailFolderNodeTreeProps } from 'owa-mail-folder-view';
import { MailFolderNode, MailFolderNodeChildren } from 'owa-mail-folder-view';
import type { MailFolder } from 'owa-graph-schema';

/**
 * Component responsible for rendering the Node and then its children
 */
const defaultExport: React.FunctionComponent<MailFolderNodeTreeProps> = observer(
    function MailFolderNodeTree(props: MailFolderNodeTreeProps) {
        const {
            folderId,
            isFavoritesSupported,
            nestDepth,
            onContextMenu,
            treeType,
            mailboxInfo,
            distinguishedFolderParentIds,
            moveFolder,
            renderFolderNode,
            deleteFolder,
            ellipsesOnHover,
            parentRef,
        } = props;

        const nodeRef = React.useRef(null);
        const folderNodeRef = React.useRef(null);
        const folder = getFolderTable().get(folderId);

        const newDistinguishedFolderParentIds = React.useMemo(() => {
            const idsToUse = distinguishedFolderParentIds ? [...distinguishedFolderParentIds] : [];
            if (folder?.distinguishedFolderType) {
                idsToUse.push(folder.distinguishedFolderType);
            }
            return idsToUse;
        }, [distinguishedFolderParentIds, folder?.distinguishedFolderType]);

        if (!folder) {
            return null;
        }

        /**
         * Add child nodes of this node if the node is expanded
         */
        const viewState = getFolderViewStateFromIdNoFallback(folderId);
        const hasChildFolders = !!(folder.childFolderIds && folder.childFolderIds.length > 0);
        const shouldRenderSubTree = viewState?.isExpanded && hasChildFolders;
        const effectiveFolderDisplayName = getEffectiveFolderDisplayName(folder);

        return (
            <>
                {/* Render folder node tree*/}
                <MailFolderNode
                    key={folderId}
                    depth={nestDepth}
                    folderId={folderId}
                    isBeingDragged={viewState?.drag?.isBeingDragged}
                    onContextMenu={onContextMenu}
                    shouldHideToggleFavorite={!isFavoritesSupported}
                    treeType={treeType}
                    folder={folder as MailFolder}
                    mailboxInfo={mailboxInfo}
                    isFolderExpandable={hasChildFolders}
                    effectiveFolderDisplayName={effectiveFolderDisplayName}
                    distinguishedFolderParentIds={distinguishedFolderParentIds}
                    moveFolder={moveFolder}
                    deleteFolder={deleteFolder}
                    ellipsesOnHover={ellipsesOnHover}
                    folderNodeRef={folderNodeRef}
                    parentRef={parentRef}
                />

                {/* Render sub node tree*/}
                <CSSTransition
                    in={!!shouldRenderSubTree}
                    timeout={200}
                    unmountOnExit={true}
                    classNames={transitionStyles}
                    nodeRef={nodeRef}
                >
                    <div className={animationContainer} ref={nodeRef}>
                        <div className={contentContainer}>
                            <MailFolderNodeChildren
                                folderIdsToRender={folder.childFolderIds ?? emptyArray}
                                nestDepth={nestDepth + 1}
                                treeType={treeType}
                                isFavoritesSupported={isFavoritesSupported}
                                onContextMenu={onContextMenu}
                                renderFolderNode={renderFolderNode}
                                distinguishedFolderParentIds={newDistinguishedFolderParentIds}
                                parentRef={folderNodeRef}
                            />
                        </div>
                    </div>
                </CSSTransition>
            </>
        );
    },
    'MailFolderNodeTree'
);
export default defaultExport;

const emptyArray: string[] = [];
